import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alan, Buton, Iskelet, Kart, MetinAlani, OnayKutusu, Rozet } from "@koclukpanel/ui";
import { ApiHatasi } from "./api";
import {
  fiyatOnerisi,
  urunArsivle,
  urunGuncelle,
  urunOlustur,
  yonetimUrunleri,
} from "./api-magaza";
import { tlYazisi } from "./magaza-etiketleri";
import type { FiyatOnerisi, UrunFormu, YonetimUrunu } from "./magaza-tipleri";

/**
 * MAĞAZA YÖNETİMİ — F-GAM-16, oyunlaştırma dalga 2 / sürüm 7a. İzin: `oyun.urun.yonet` (ekranı çağıran karar verir).
 *
 * Ürün listesi, ekleme/düzenleme ve arşivleme. FİYATI YÖNETİCİ GİRER; sistem yalnız ÖNERİR (Emre, dalga-2-tasarim
 * §3.2): "Öneriyi fiyata yaz" düğmesi puan alanını DOLDURUR ama KAYDETMEZ. Önerilen puan kargo ücretini içerir
 * (kargoyu kurum öder, Emre S2). Maliyet (TL) yalnız bu ekranda görünür; öğrenciye hiç dönmez.
 * Fiyat değişikliği eski siparişi etkilemez (sipariş fiyatı kopyalar) ve stok, fiyat, arşiv kararları SUNUCUDA.
 */
export function MagazaYonetimi() {
  const [duzenlenen, setDuzenlenen] = useState<YonetimUrunu | "yeni" | null>(null);
  const q = useQuery({ queryKey: ["magaza-urunleri"], queryFn: yonetimUrunleri, retry: false });
  return (
    <div className="flex flex-col gap-4">
      <Kart
        baslik="Mağaza ürünleri"
        aciklama="Öğrenciler puanla bu ürünleri sipariş eder. Kargo ücretini kurum öder; puan fiyatı kargoyu içerir."
        eylem={
          duzenlenen === null ? (
            <Buton gorunum="ikincil" onClick={() => setDuzenlenen("yeni")}>
              Yeni ürün
            </Buton>
          ) : undefined
        }
      >
        {q.isPending ? (
          <Iskelet satir={3} />
        ) : q.error ? (
          <p role="alert" className="text-hata-700 text-sm">
            Ürünler alınamadı.
          </p>
        ) : q.data.length === 0 ? (
          <p className="text-notr-600 text-sm">
            Henüz ürün yok. &ldquo;Yeni ürün&rdquo; ile ekleyin.
          </p>
        ) : (
          <ul className="divide-notr-100 divide-y text-sm">
            {q.data.map((u) => (
              <UrunSatiri
                key={u.id}
                urun={u}
                duzenleniyor={duzenlenen !== null}
                onDuzenle={setDuzenlenen}
              />
            ))}
          </ul>
        )}
      </Kart>
      {duzenlenen !== null && (
        <UrunFormuEkrani
          key={duzenlenen === "yeni" ? "yeni" : duzenlenen.id}
          urun={duzenlenen === "yeni" ? null : duzenlenen}
          onKapat={() => setDuzenlenen(null)}
        />
      )}
    </div>
  );
}

function hataYazisi(e: unknown, varsayilan: string): string {
  return e instanceof ApiHatasi ? e.message : varsayilan;
}

function UrunSatiri({
  urun,
  duzenleniyor,
  onDuzenle,
}: {
  urun: YonetimUrunu;
  duzenleniyor: boolean;
  onDuzenle: (u: YonetimUrunu) => void;
}) {
  const qc = useQueryClient();
  const [onay, setOnay] = useState(false);
  const mut = useMutation({
    mutationFn: () => urunArsivle(urun.id),
    onSuccess: () => {
      setOnay(false);
      void qc.invalidateQueries({ queryKey: ["magaza-urunleri"] });
    },
  });
  return (
    <li className="flex flex-col gap-2 py-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-notr-900 font-medium">{urun.ad}</span>
          {urun.arsivlendi ? (
            <Rozet>Arşivli</Rozet>
          ) : urun.etkin ? (
            <Rozet ton="onay">Etkin</Rozet>
          ) : (
            <Rozet>Pasif</Rozet>
          )}
          {!urun.arsivlendi && urun.stok === 0 && <Rozet ton="hata">Tükendi</Rozet>}
        </div>
        {!urun.arsivlendi && (
          <div className="flex gap-2">
            <Buton
              gorunum="sessiz"
              aria-label={`${urun.ad} ürününü düzenle`}
              disabled={duzenleniyor}
              onClick={() => onDuzenle(urun)}
            >
              Düzenle
            </Buton>
            {!onay && (
              <Buton
                gorunum="sessiz"
                aria-label={`${urun.ad} ürününü arşivle`}
                onClick={() => setOnay(true)}
              >
                Arşivle
              </Buton>
            )}
          </div>
        )}
      </div>
      <dl className="text-notr-600 flex flex-wrap gap-x-5 gap-y-1 text-xs">
        <div className="flex gap-1">
          <dt>Fiyat:</dt>
          <dd className="text-notr-900 tabular-nums">{urun.puanFiyati} puan</dd>
        </div>
        <div className="flex gap-1">
          <dt>Stok:</dt>
          <dd className="text-notr-900 tabular-nums">{urun.stok}</dd>
        </div>
        <div className="flex gap-1">
          <dt>Maliyet:</dt>
          <dd className="text-notr-900 tabular-nums">
            {urun.maliyetTl === null ? "girilmedi" : tlYazisi(urun.maliyetTl)}
          </dd>
        </div>
        {urun.kisiBasiSinir !== null && (
          <div className="flex gap-1">
            <dt>Kişi başı sınır:</dt>
            <dd className="text-notr-900 tabular-nums">{urun.kisiBasiSinir}</dd>
          </div>
        )}
      </dl>
      {onay && (
        <div role="group" aria-label={`${urun.ad} arşivleme onayı`} className="flex flex-col gap-2">
          <p className="text-notr-700 text-sm">
            Arşivlenen ürün vitrinden kalkar; verilmiş siparişler etkilenmez. Arşivlensin mi?
          </p>
          {mut.isError && (
            <p role="alert" className="text-hata-700 text-xs">
              {hataYazisi(mut.error, "Ürün arşivlenemedi.")}
            </p>
          )}
          <div className="flex gap-2">
            <Buton gorunum="tehlike" yukleniyor={mut.isPending} onClick={() => mut.mutate()}>
              Evet, arşivle
            </Buton>
            <Buton gorunum="ikincil" disabled={mut.isPending} onClick={() => setOnay(false)}>
              Vazgeç
            </Buton>
          </div>
        </div>
      )}
    </li>
  );
}

/** Boş ise null; sayı değilse NaN (geçersiz). */
function sayiOku(s: string): number | null {
  if (s.trim() === "") return null;
  return Number(s.trim().replace(",", "."));
}
const tamMi = (n: number | null): n is number => n !== null && Number.isInteger(n);
const metne = (n: number | string | null | undefined): string =>
  n === null || n === undefined ? "" : String(n);
/** Sözleşme TL'yi METİN ister ("12.50"): para kayan noktayla taşınmaz (CLAUDE.md kural 8). */
const tlMetni = (n: number | null): string | null => (n === null ? null : n.toFixed(2));

function UrunFormuEkrani({ urun, onKapat }: { urun: YonetimUrunu | null; onKapat: () => void }) {
  const qc = useQueryClient();
  const [ad, setAd] = useState(urun?.ad ?? "");
  const [aciklama, setAciklama] = useState(urun?.aciklama ?? "");
  const [puan, setPuan] = useState(urun ? String(urun.puanFiyati) : "");
  const [stok, setStok] = useState(urun ? String(urun.stok) : "0");
  const [maliyet, setMaliyet] = useState(metne(urun?.maliyetTl));
  const [hedefAy, setHedefAy] = useState(metne(urun?.hedefAy));
  const [sinir, setSinir] = useState(metne(urun?.kisiBasiSinir));
  const [sira, setSira] = useState(urun ? String(urun.sira) : "0");
  const [etkin, setEtkin] = useState(urun?.etkin ?? true);
  const [fiyataYazildi, setFiyataYazildi] = useState(false);

  const puanN = sayiOku(puan);
  const stokN = sayiOku(stok);
  const maliyetN = sayiOku(maliyet);
  const ayN = sayiOku(hedefAy);
  const sinirN = sayiOku(sinir);
  const siraN = sayiOku(sira);

  const puanHatasi =
    tamMi(puanN) && puanN >= 1 ? undefined : "Puan fiyatı 1 ya da daha büyük bir tam sayı olmalı.";
  const stokHatasi =
    tamMi(stokN) && stokN >= 0 ? undefined : "Stok 0 ya da daha büyük bir tam sayı olmalı.";
  const maliyetHatasi =
    maliyetN === null || (Number.isFinite(maliyetN) && maliyetN >= 0)
      ? undefined
      : "Maliyet 0 ya da daha büyük bir sayı olmalı.";
  const ayHatasi =
    ayN === null || (tamMi(ayN) && ayN >= 1 && ayN <= 24)
      ? undefined
      : "Ay sayısı 1 ile 24 arasında tam sayı olmalı.";
  const sinirHatasi =
    sinirN === null || (tamMi(sinirN) && sinirN >= 1)
      ? undefined
      : "Sınır 1 ya da daha büyük bir tam sayı olmalı.";
  const siraHatasi = tamMi(siraN) ? undefined : "Sıralama ağırlığı tam sayı olmalı.";
  const gecerli =
    ad.trim().length > 0 &&
    !puanHatasi &&
    !stokHatasi &&
    !maliyetHatasi &&
    !ayHatasi &&
    !sinirHatasi &&
    !siraHatasi;

  const oneri = useMutation<FiyatOnerisi, unknown, void>({
    mutationFn: () => fiyatOnerisi(ayN as number, tlMetni(maliyetN) ?? undefined),
  });
  const kaydet = useMutation({
    mutationFn: () => {
      const govde: UrunFormu = {
        ad: ad.trim(),
        aciklama: aciklama.trim() === "" ? null : aciklama.trim(),
        puanFiyati: puanN as number,
        stok: stokN as number,
        maliyetTl: tlMetni(maliyetN),
        hedefAy: ayN,
        kisiBasiSinir: sinirN,
        etkin,
        sira: siraN as number,
      };
      return urun ? urunGuncelle(urun.id, govde) : urunOlustur(govde);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["magaza-urunleri"] });
      onKapat();
    },
  });

  const oneriSonucu = oneri.data;
  const oneriIste = tamMi(ayN) && ayN >= 1 && ayN <= 24 && !maliyetHatasi;
  const oneriyiSifirla = () => {
    oneri.reset();
    setFiyataYazildi(false);
  };

  return (
    <Kart baslik={urun ? `Ürünü düzenle · ${urun.ad}` : "Yeni ürün"}>
      <form
        className="flex flex-col gap-3"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          if (gecerli && !kaydet.isPending) kaydet.mutate();
        }}
      >
        <Alan
          etiket="Ürün adı"
          zorunlu
          maxLength={120}
          value={ad}
          onChange={(e) => setAd(e.target.value)}
        />
        <MetinAlani
          etiket="Açıklama"
          rows={2}
          maxLength={1000}
          value={aciklama}
          onChange={(e) => setAciklama(e.target.value)}
        />

        <fieldset className="border-notr-200 rounded-kart flex flex-col gap-3 border p-3">
          <legend className="text-notr-900 px-1 text-sm font-medium">Fiyat önerici</legend>
          <p className="text-notr-600 text-xs">
            Ürünü kaç ayda bir öğrencinin alabilmesini istiyorsanız yazın; sistem öğrencilerin
            gerçek kazanım hızına bakarak puan önerir. Fiyatı siz girersiniz, öneri kendiliğinden
            kaydedilmez. Kargo ücretini kurum öder: maliyete kargoyu da yazın; öneri puanın yanında
            ürün + kargo maliyetini gösterir (TL puana çevrilmez).
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Alan
              etiket="Kaç ayda vermek istiyorsunuz? (ay)"
              type="number"
              inputMode="numeric"
              min={1}
              max={24}
              value={hedefAy}
              hata={ayHatasi}
              onChange={(e) => {
                setHedefAy(e.target.value);
                oneriyiSifirla();
              }}
            />
            <Alan
              etiket="Maliyet (TL, kargo dahil)"
              type="number"
              inputMode="decimal"
              min={0}
              step="0.01"
              value={maliyet}
              hata={maliyetHatasi}
              yardim="Yalnız yönetim görür; öğrenciye gösterilmez."
              onChange={(e) => {
                setMaliyet(e.target.value);
                oneriyiSifirla();
              }}
            />
          </div>
          <div>
            <Buton
              gorunum="ikincil"
              disabled={!oneriIste}
              yukleniyor={oneri.isPending}
              onClick={() => oneri.mutate()}
            >
              Fiyat öner
            </Buton>
          </div>
          {oneri.isError && (
            <p role="alert" className="text-hata-700 text-xs">
              {hataYazisi(oneri.error, "Fiyat önerisi alınamadı.")}
            </p>
          )}
          {oneriSonucu && (
            <div
              role="status"
              className="bg-marka-50 text-notr-900 rounded-kart flex flex-col gap-1 p-3 text-sm"
            >
              <p>
                Önerilen fiyat:{" "}
                <strong className="tabular-nums">{oneriSonucu.onerilen} puan</strong>{" "}
                {oneriSonucu.enHizliAy !== null && (
                  <span className="text-notr-700">
                    (en hızlı öğrenciler {oneriSonucu.enHizliAy} ayda alır)
                  </span>
                )}
              </p>
              {oneriSonucu.toplamMaliyetTl !== null && (
                <p className="text-notr-700">
                  Ürün + kargo maliyeti: {tlYazisi(oneriSonucu.toplamMaliyetTl)}
                </p>
              )}
              {oneriSonucu.yontem === "KURAL" && (
                <p className="text-notr-700">
                  Henüz yeterli veri yok; öneri kural sürümünden hesaplandı.
                </p>
              )}
              <div className="mt-1 flex items-center gap-3">
                <Buton
                  gorunum="ikincil"
                  onClick={() => {
                    setPuan(String(oneriSonucu.onerilen));
                    setFiyataYazildi(true);
                  }}
                >
                  Öneriyi fiyata yaz
                </Buton>
                {fiyataYazildi && (
                  <span className="text-notr-700 text-xs">
                    Fiyat alanına yazıldı; ürünü kaydedene kadar geçerli olmaz.
                  </span>
                )}
              </div>
            </div>
          )}
        </fieldset>

        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <Alan
            etiket="Puan fiyatı"
            zorunlu
            type="number"
            inputMode="numeric"
            min={1}
            value={puan}
            hata={puan === "" ? undefined : puanHatasi}
            yardim="Kargo dahil. Fiyat değişikliği verilmiş siparişleri etkilemez."
            onChange={(e) => {
              setPuan(e.target.value);
              setFiyataYazildi(false);
            }}
          />
          <Alan
            etiket="Stok"
            zorunlu
            type="number"
            inputMode="numeric"
            min={0}
            value={stok}
            hata={stokHatasi}
            yardim="0 olunca vitrinde “tükendi” görünür."
            onChange={(e) => setStok(e.target.value)}
          />
          <Alan
            etiket="Kişi başına sınır"
            type="number"
            inputMode="numeric"
            min={1}
            value={sinir}
            hata={sinirHatasi}
            yardim="Boş bırakırsanız sınırsız."
            onChange={(e) => setSinir(e.target.value)}
          />
        </div>
        <Alan
          etiket="Sıralama ağırlığı"
          type="number"
          inputMode="numeric"
          value={sira}
          hata={siraHatasi}
          onChange={(e) => setSira(e.target.value)}
        />
        <OnayKutusu
          etiket="Mağazada etkin"
          yardim="Kapalıyken ürün vitrinde görünmez; arşivden farkı, sonra yeniden açabilmenizdir."
          checked={etkin}
          onChange={(e) => setEtkin(e.target.checked)}
        />

        {kaydet.isError && (
          <p role="alert" className="text-hata-700 text-xs">
            {hataYazisi(kaydet.error, "Ürün kaydedilemedi.")}
          </p>
        )}
        <div className="flex gap-2">
          <Buton type="submit" disabled={!gecerli} yukleniyor={kaydet.isPending}>
            {urun ? "Değişiklikleri kaydet" : "Ürünü ekle"}
          </Buton>
          <Buton type="button" gorunum="ikincil" disabled={kaydet.isPending} onClick={onKapat}>
            Vazgeç
          </Buton>
        </div>
      </form>
    </Kart>
  );
}
